/**
 * AppSettingsContext
 *
 * Application-wide preferences configured from the admin Settings page.
 * These values apply to every visitor of the app — the source of truth is
 * the server (see public/api/settings.php + the `app_settings` table).
 *
 * Strategy
 *   1. On mount: hydrate synchronously from a localStorage cache so the UI
 *      paints with the most-recently-seen server state (no flash-of-default).
 *   2. Then: fetch from the server and overwrite the cache with the fresh
 *      values. Errors are swallowed — the cache (or defaults) keeps the app
 *      working offline.
 *   3. On update: PUT to the server and wait for the server's reply before
 *      committing to local state. No optimistic UI — an admin who doesn't
 *      see the change land should know the write failed.
 *
 * Any per-device / per-user preferences should NOT live here. They belong
 * in their own context or in localStorage directly.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import {
  TransitionType,
  DEFAULT_TRANSITION,
  DEFAULT_TRANSITION_DURATION_MS,
  DEFAULT_CYCLE_INTERVAL_MS,
  DEFAULT_AUTO_PLAY,
  isTransitionType,
  clampTransitionDuration,
  clampCycleInterval,
  normalizeAutoPlay,
} from '../config/transitions';
import { fetchAppSettings, updateAppSettings as apiUpdateAppSettings } from '../services/api';
import { normalizeSpaceAccess, type SpaceAccess } from '../data/spaceAccess';
import { DEFAULT_CARD_STYLE, normalizeCardStyle, type EstimateCardStyle } from '../components/results/cardStyle';
import {
  DEFAULT_DOWNLOAD_BUTTON, DEFAULT_WATERMARK, normalizeDownloadButton, normalizeWatermark, type DownloadButtonSettings, type WatermarkSettings,
} from '../components/results/watermark';

export interface AppSettings {
  /** Default image transition used by the results visualizer. */
  defaultTransition: TransitionType;
  /** Transition animation duration in milliseconds. */
  transitionDurationMs: number;
  /** How long each visualization holds before the next auto-cycle swap. */
  cycleIntervalMs: number;
  /** When true, the results visualizer starts in Play mode automatically. */
  autoPlayEnabled: boolean;
  /**
   * The results page's hero card: corners, border, shadow, the glass of its
   * panels, and the animation over a designed room's still while its first
   * photoreal picture renders (components/results/cardStyle.ts). Its `scope`
   * says whether the card styling is for designed rooms only or every estimate.
   */
  estimateCardStyle: EstimateCardStyle;
  /** The mark stamped on the estimate pictures, server-side (components/results/watermark.ts). */
  watermark: WatermarkSettings;
  /** The button over the estimate pictures that saves the one shown. */
  downloadButton: DownloadButtonSettings;
  /**
   * Master runtime switch for Cloudflare Turnstile. When false, both the
   * guest-estimate and comment forms skip the widget and server-side
   * verification — useful for local dev on non-FQDN hosts where Turnstile
   * can't load. The env-based REACT_APP_TURNSTILE_SITE_KEY still gates
   * whether Turnstile is *available* at all; this toggle disables it on top.
   */
  turnstileEnabled: boolean;
  /** Pardot form handler endpoint URL. Empty string disables submission. */
  pardotFormHandlerUrl: string;
  /** When true, the server POSTs the guest-estimate payload to Pardot. */
  pardotEnabled: boolean;
  /** Value sent as the `FriendlyName` Pardot field (form origin label). */
  pardotFriendlyName: string;
  /**
   * When true, the server returns the exact Pardot payload in the
   * /api/estimates response and the client logs it to the browser
   * console — regardless of whether `pardotEnabled` is on. Use to
   * review payload shape before flipping Pardot on in production.
   */
  pardotDebug: boolean;
  /**
   * Acronyms a retailer must carry (active product per acronym) to be
   * eligible for retailer-priced estimate shares. Editable in
   * /admin/settings.
   */
  requiredAcronyms: string[];
  /** YouTube URL of the tutorial video shown in the lightbox. Empty disables the feature. */
  tutorialVideoUrl: string;
  /** Text used for the small "watch the tutorial" link on the main UI (under the step description). */
  tutorialMainLinkText: string;
  /** Label shown for the tutorial entry in the nav menu (desktop dropdown + mobile collapse). */
  tutorialMenuLinkText: string;
  /** RGBA backdrop colour rendered behind the lightbox. */
  tutorialBackdropColor: { r: number; g: number; b: number; a: number };
  /** Blur amount (px) applied to everything behind the backdrop. 0 disables. */
  tutorialBackdropBlurPx: number;
  /**
   * Address input mode on the guest-estimate form. 'classic' = the country +
   * region selects; 'autocomplete' = a full-address typeahead (Address One/Two,
   * Territory) backed by addressProvider via the server proxy.
   */
  addressInputMode: 'classic' | 'autocomplete';
  /** Autocomplete provider (server-side proxy chooses the API). Extensible. */
  addressProvider: string;
  /**
   * When true, the autocomplete proxy restricts suggestions to the countries
   * configured + active under /admin/locations (helps filter noise to your
   * served markets). Applied server-side via the provider's country filter.
   */
  addressRestrictToCountries: boolean;
  /**
   * When true (autocomplete mode), selecting an address with no street/building
   * number shows a non-blocking "looks incomplete" hint. Submission is never
   * blocked — it just nudges for better data quality.
   */
  addressNudgeIncomplete: boolean;
  /**
   * Application timezone (IANA identifier, e.g. 'America/Toronto'). Estimate
   * and comment timestamps are stored as server-local wall-clock in this zone;
   * admin views label them with it. Configurable in /admin/settings.
   */
  timezone: string;
  /**
   * Read-only: whether an address API key is configured server-side. The key
   * value itself is never sent to the browser (the proxy uses it server-side).
   */
  addressApiKeySet: boolean;
  /**
   * Write-only: a new address API key to store. Never populated from the
   * server (stays undefined in state) — only set in an update patch from the
   * admin Settings page. See addressApiKeySet for whether one exists.
   * @deprecated post geoKey_<provider> instead; the server maps this onto it.
   */
  addressApiKey?: string;
  /**
   * Geocoding registry (public/api/geo-providers). Provider for the location-map
   * search box; '' = same as addressProvider.
   */
  mapSearchProvider: string;
  /** Read-only: which keyed providers have a stored key (never the keys). */
  geoKeysSet: Record<string, boolean>;
  /** Write-only per-provider keys (google/here/geoapify/locationiq/radar). '' clears. */
  geoKey_google?: string;
  geoKey_here?: string;
  geoKey_geoapify?: string;
  geoKey_locationiq?: string;
  geoKey_radar?: string;
  geoPhotonEndpoint: string;
  geoNominatimEndpoint: string;
  geoNominatimEmail: string;
  /** Bias type-ahead toward the visitor's IP location when no explicit point is given. */
  geoBiasIp: boolean;
  /** Server-side cache TTL for geocoding answers (seconds). */
  geoCacheSeconds: number;
  /** Per-IP requests per minute on the geocoding proxy (0 = off). */
  geoRateLimitPerMin: number;
  /**
   * 3D designer: the least distance, in inches, a window or door may sit from
   * the perimeter of its wall (doors stand on the floor, so not their bottom).
   */
  openingSafeMarginIn: number;
  /**
   * Advanced mode: the floor planner + 3D designer offered as a 4th project
   * type on step 1 (route /design). Off, it is still reachable by beta testers
   * who flip the hidden hot key (see utils/advancedDesignerFlag.ts).
   */
  advancedDesignerEnabled: boolean;
  /** Advanced mode: allow more than one room per blueprint (the Draw tool). */
  advancedDesignerMultiRoom: boolean;
  /**
   * Queue a photoreal Blender render when an estimate from the 3D designer is
   * saved (needs the GPU worker running somewhere). Off: the quick preview only.
   */
  renderQueueEnabled: boolean;
  /** Renders one person may have unfinished at once (a newer one stops the oldest); 0 = no limit. Admins, retailers' users and `render-unlimited` holders are never limited. */
  renderActiveLimit: number;
  /** Renders one person may queue in 24 hours; 0 = no limit. */
  renderDailyLimit: number;
  /** Where each space type is offered (data/spaceAccess.ts): by wizard mode and Advanced. Missing = offered. */
  spaceTypeAccess: SpaceAccess;
  /** IP geolocation provider for map centring / bias. */
  geoIpProvider: 'ip-api' | 'none';
  /**
   * Location maps. The upstream (trusscore.com) endpoint URL + a write-only shared key
   * (GET surfaces mapsUpstreamKeySet). mapsTileApiKey is PUBLIC by design — the browser
   * fetches vector tiles with it, so it must be an HTTP-referrer-restricted key.
   */
  mapsUpstreamBaseUrl: string;
  mapsUpstreamKey?: string;
  mapsUpstreamKeySet: boolean;
  mapsDataCacheTtl: number;
  mapsImageCacheTtl: number;
  mapsDefaultTileStyleUrl: string;
  mapsTileApiKey: string;
  mapsDefaultUnits: 'km' | 'miles';
  /**
   * Keycloak SSO — effective issuer / client id (app_settings override resolved
   * over the config.php fallback). Editable in /admin/settings (super-only).
   */
  oidcIssuer: string;
  oidcClientId: string;
  /** Read-only: whether an OIDC client secret is configured server-side. */
  oidcClientSecretSet: boolean;
  /** Read-only: whether SSO is fully configured (issuer + client id present). */
  oidcConfigured: boolean;
  /**
   * MASTER switch for the whole accounts/login system. OFF (default) = guest-only
   * app + password admin login (today's behavior); ON = Keycloak admin + end-user
   * accounts UI. Public flag so the SPA + admin gate know the mode pre-auth. Sits
   * above nativeAuthEnabled. Super-only to edit; server refuses ON without OIDC.
   */
  accountsEnabled: boolean;
  /**
   * When true, the app shows a native in-app login/signup UI (AJAX + modals)
   * instead of redirecting to Keycloak's hosted pages. Public flag so the SPA
   * can pick the login UI before the user authenticates. Super-only to edit.
   */
  nativeAuthEnabled: boolean;
  /**
   * Write-only: a new OIDC client secret to store (app_settings, server-side).
   * Never hydrated from the server — only sent in an update patch.
   */
  oidcClientSecret?: string;
  /**
   * Gleap chat widget (src/components/GleapChat.tsx). All public — the SDK key is
   * a browser key. Chat leads reach Pardot via the trusscore.com Gleap webhook
   * receiver; the estimator stores nothing.
   */
  gleapEnabled: boolean;
  gleapSdkKey: string;
  /** Show the widget on branded retailer partner portals. */
  gleapPartnerPortals: boolean;
  /** Extra route prefixes that hide the widget ('/admin' is always hidden). */
  gleapExcludePaths: string[];
  /** Keep the widget language in step with the app's i18n locale. */
  gleapSyncLanguage: boolean;
  /** Attach the Microsoft Clarity session URL to the ticket on widget open. */
  gleapClarityLink: boolean;
}

const DEFAULTS: AppSettings = {
  defaultTransition: DEFAULT_TRANSITION,
  transitionDurationMs: DEFAULT_TRANSITION_DURATION_MS,
  cycleIntervalMs: DEFAULT_CYCLE_INTERVAL_MS,
  autoPlayEnabled: DEFAULT_AUTO_PLAY,
  estimateCardStyle: DEFAULT_CARD_STYLE,
  watermark: DEFAULT_WATERMARK,
  downloadButton: DEFAULT_DOWNLOAD_BUTTON,
  turnstileEnabled: true,
  pardotFormHandlerUrl: '',
  pardotEnabled: false,
  pardotFriendlyName: 'Cost & Materials Estimator',
  pardotDebug: false,
  requiredAcronyms: ['W&CB', 'SW', 'H', 'J1/2', 'J3/4', 'JSnap', 'Base', 'OCR', 'TC'],
  tutorialVideoUrl: 'https://www.youtube.com/watch?v=6hUONwYVf9I',
  // Empty means "not overridden by an admin", NOT "blank". The front end
  // falls back to a translated default (common:tutorial.mainLink /
  // common:menu.tutorial); keeping English text here would have pinned these
  // two strings to English forever, since a value that is always present can
  // never fall back. An admin's own text is database content and renders as
  // authored (translated via the content overlay, entity_type 'app_settings').
  tutorialMainLinkText: '',
  tutorialMenuLinkText: '',
  tutorialBackdropColor: { r: 0, g: 0, b: 0, a: 0.75 },
  tutorialBackdropBlurPx: 0,
  addressInputMode: 'classic',
  addressProvider: 'here',
  addressRestrictToCountries: false,
  addressNudgeIncomplete: false,
  timezone: 'America/Toronto',
  addressApiKeySet: false,
  mapSearchProvider: '',
  geoKeysSet: {},
  geoPhotonEndpoint: 'https://photon.komoot.io',
  geoNominatimEndpoint: 'https://nominatim.openstreetmap.org',
  geoNominatimEmail: '',
  geoBiasIp: true,
  geoCacheSeconds: 86400,
  geoRateLimitPerMin: 60,
  openingSafeMarginIn: 4,
  advancedDesignerEnabled: false,
  advancedDesignerMultiRoom: false,
  renderQueueEnabled: false,
  renderActiveLimit: 1,
  renderDailyLimit: 10,
  spaceTypeAccess: {},
  geoIpProvider: 'ip-api',
  mapsUpstreamBaseUrl: '',
  mapsUpstreamKeySet: false,
  mapsDataCacheTtl: 180,
  mapsImageCacheTtl: 604800,
  mapsDefaultTileStyleUrl: 'https://tiles.openfreemap.org/styles/liberty',
  mapsTileApiKey: '',
  mapsDefaultUnits: 'km',
  oidcIssuer: '',
  oidcClientId: '',
  oidcClientSecretSet: false,
  oidcConfigured: false,
  accountsEnabled: false,
  nativeAuthEnabled: false,
  gleapEnabled: false,
  gleapSdkKey: '',
  gleapPartnerPortals: true,
  gleapExcludePaths: [],
  gleapSyncLanguage: true,
  gleapClarityLink: true,
};

/**
 * The English wording the front end falls back to when an admin hasn't set
 * their own. Exported ONLY for the admin settings placeholders, so an unset
 * field shows what it will actually render rather than looking blank. The
 * front end reads the i18n dictionary, not this — these two must stay in step
 * with common:tutorial.mainLink and common:menu.tutorial.
 */
export const TUTORIAL_DEFAULTS_EN = {
  main: 'Need Help? Watch our tutorial by clicking here.',
  menu: 'Tutorial Video',
};


/** localStorage cache key. Bump the suffix to invalidate stale caches. */
const CACHE_KEY = 'trusscore.app.settings.cache.v1';

interface AppSettingsContextValue {
  settings: AppSettings;
  /** True while the initial server fetch is in flight. */
  isLoading: boolean;
  /** Non-null when the most recent server interaction failed. */
  error: string | null;
  /** Persists the patch to the server. Resolves with the new merged state. */
  updateSettings: (patch: Partial<AppSettings>) => Promise<AppSettings>;
  /** Resets every setting to its default, server-side. */
  resetSettings: () => Promise<AppSettings>;
}

const AppSettingsContext = createContext<AppSettingsContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Local cache helpers
// ---------------------------------------------------------------------------

function normalizeBool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1' || v === 'true') return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return fallback;
}

function normalize(raw: any): AppSettings {
  return {
    defaultTransition: isTransitionType(raw?.defaultTransition)
      ? raw.defaultTransition
      : DEFAULTS.defaultTransition,
    transitionDurationMs: clampTransitionDuration(raw?.transitionDurationMs),
    cycleIntervalMs: clampCycleInterval(raw?.cycleIntervalMs),
    autoPlayEnabled: normalizeAutoPlay(raw?.autoPlayEnabled),
    estimateCardStyle: normalizeCardStyle(raw?.estimateCardStyle),
    watermark: normalizeWatermark(raw?.watermark),
    downloadButton: normalizeDownloadButton(raw?.downloadButton),
    turnstileEnabled: normalizeBool(raw?.turnstileEnabled, DEFAULTS.turnstileEnabled),
    pardotFormHandlerUrl:
      typeof raw?.pardotFormHandlerUrl === 'string'
        ? raw.pardotFormHandlerUrl
        : DEFAULTS.pardotFormHandlerUrl,
    pardotEnabled: normalizeBool(raw?.pardotEnabled, DEFAULTS.pardotEnabled),
    pardotFriendlyName:
      typeof raw?.pardotFriendlyName === 'string' && raw.pardotFriendlyName.trim() !== ''
        ? raw.pardotFriendlyName
        : DEFAULTS.pardotFriendlyName,
    pardotDebug: normalizeBool(raw?.pardotDebug, DEFAULTS.pardotDebug),
    requiredAcronyms: Array.isArray(raw?.requiredAcronyms)
      ? raw.requiredAcronyms.filter((s: unknown): s is string => typeof s === 'string' && s.trim() !== '').map((s: string) => s.trim())
      : DEFAULTS.requiredAcronyms,
    tutorialVideoUrl:
      typeof raw?.tutorialVideoUrl === 'string'
        ? raw.tutorialVideoUrl
        : DEFAULTS.tutorialVideoUrl,
    tutorialMainLinkText:
      typeof raw?.tutorialMainLinkText === 'string' && raw.tutorialMainLinkText.trim() !== ''
        ? raw.tutorialMainLinkText
        : DEFAULTS.tutorialMainLinkText,
    tutorialMenuLinkText:
      typeof raw?.tutorialMenuLinkText === 'string' && raw.tutorialMenuLinkText.trim() !== ''
        ? raw.tutorialMenuLinkText
        : DEFAULTS.tutorialMenuLinkText,
    tutorialBackdropColor: normalizeRgba(raw?.tutorialBackdropColor, DEFAULTS.tutorialBackdropColor),
    tutorialBackdropBlurPx: clampBlur(raw?.tutorialBackdropBlurPx),
    addressInputMode: raw?.addressInputMode === 'autocomplete' ? 'autocomplete' : 'classic',
    addressProvider:
      typeof raw?.addressProvider === 'string' && raw.addressProvider.trim() !== ''
        ? raw.addressProvider.trim()
        : DEFAULTS.addressProvider,
    addressRestrictToCountries: normalizeBool(raw?.addressRestrictToCountries, false),
    addressNudgeIncomplete: normalizeBool(raw?.addressNudgeIncomplete, false),
    timezone:
      typeof raw?.timezone === 'string' && raw.timezone.trim() !== ''
        ? raw.timezone.trim()
        : DEFAULTS.timezone,
    addressApiKeySet: normalizeBool(raw?.addressApiKeySet, false),
    // addressApiKey / geoKey_* are write-only — never hydrated from the server.
    mapSearchProvider: typeof raw?.mapSearchProvider === 'string' ? raw.mapSearchProvider.trim() : DEFAULTS.mapSearchProvider,
    geoKeysSet: raw?.geoKeysSet && typeof raw.geoKeysSet === 'object' && !Array.isArray(raw.geoKeysSet)
      ? Object.fromEntries(Object.entries(raw.geoKeysSet as Record<string, unknown>).map(([k, v]) => [k, normalizeBool(v, false)]))
      : DEFAULTS.geoKeysSet,
    geoPhotonEndpoint: typeof raw?.geoPhotonEndpoint === 'string' && raw.geoPhotonEndpoint.trim() !== '' ? raw.geoPhotonEndpoint.trim() : DEFAULTS.geoPhotonEndpoint,
    geoNominatimEndpoint: typeof raw?.geoNominatimEndpoint === 'string' && raw.geoNominatimEndpoint.trim() !== '' ? raw.geoNominatimEndpoint.trim() : DEFAULTS.geoNominatimEndpoint,
    geoNominatimEmail: typeof raw?.geoNominatimEmail === 'string' ? raw.geoNominatimEmail.trim() : DEFAULTS.geoNominatimEmail,
    geoBiasIp: normalizeBool(raw?.geoBiasIp, DEFAULTS.geoBiasIp),
    geoCacheSeconds: Math.round(clampNum(raw?.geoCacheSeconds, 0, 604800, DEFAULTS.geoCacheSeconds)),
    geoRateLimitPerMin: Math.round(clampNum(raw?.geoRateLimitPerMin, 0, 1000, DEFAULTS.geoRateLimitPerMin)),
    openingSafeMarginIn: Math.round(clampNum(raw?.openingSafeMarginIn, 0, 24, DEFAULTS.openingSafeMarginIn) * 100) / 100,
    advancedDesignerEnabled: normalizeBool(raw?.advancedDesignerEnabled, DEFAULTS.advancedDesignerEnabled),
    advancedDesignerMultiRoom: normalizeBool(raw?.advancedDesignerMultiRoom, DEFAULTS.advancedDesignerMultiRoom),
    renderQueueEnabled: normalizeBool(raw?.renderQueueEnabled, DEFAULTS.renderQueueEnabled),
    renderActiveLimit: Math.round(clampNum(raw?.renderActiveLimit, 0, 100, DEFAULTS.renderActiveLimit)),
    renderDailyLimit: Math.round(clampNum(raw?.renderDailyLimit, 0, 1000, DEFAULTS.renderDailyLimit)),
    spaceTypeAccess: normalizeSpaceAccess(raw?.spaceTypeAccess),
    geoIpProvider: raw?.geoIpProvider === 'none' ? 'none' : 'ip-api',
    mapsUpstreamBaseUrl: typeof raw?.mapsUpstreamBaseUrl === 'string' ? raw.mapsUpstreamBaseUrl.trim() : DEFAULTS.mapsUpstreamBaseUrl,
    mapsUpstreamKeySet: normalizeBool(raw?.mapsUpstreamKeySet, false),
    mapsDataCacheTtl: Math.round(clampNum(raw?.mapsDataCacheTtl, 0, 3600, DEFAULTS.mapsDataCacheTtl)),
    mapsImageCacheTtl: Math.round(clampNum(raw?.mapsImageCacheTtl, 0, 2592000, DEFAULTS.mapsImageCacheTtl)),
    mapsDefaultTileStyleUrl: typeof raw?.mapsDefaultTileStyleUrl === 'string' && raw.mapsDefaultTileStyleUrl.trim() !== '' ? raw.mapsDefaultTileStyleUrl.trim() : DEFAULTS.mapsDefaultTileStyleUrl,
    mapsTileApiKey: typeof raw?.mapsTileApiKey === 'string' ? raw.mapsTileApiKey.trim() : DEFAULTS.mapsTileApiKey,
    mapsDefaultUnits: raw?.mapsDefaultUnits === 'miles' ? 'miles' : 'km',
    oidcIssuer: typeof raw?.oidcIssuer === 'string' ? raw.oidcIssuer : DEFAULTS.oidcIssuer,
    oidcClientId: typeof raw?.oidcClientId === 'string' ? raw.oidcClientId : DEFAULTS.oidcClientId,
    oidcClientSecretSet: normalizeBool(raw?.oidcClientSecretSet, false),
    oidcConfigured: normalizeBool(raw?.oidcConfigured, false),
    // oidcClientSecret is write-only — never hydrated from the server.
    accountsEnabled: normalizeBool(raw?.accountsEnabled, DEFAULTS.accountsEnabled),
    nativeAuthEnabled: normalizeBool(raw?.nativeAuthEnabled, DEFAULTS.nativeAuthEnabled),
    gleapEnabled: normalizeBool(raw?.gleapEnabled, DEFAULTS.gleapEnabled),
    gleapSdkKey: typeof raw?.gleapSdkKey === 'string' ? raw.gleapSdkKey.trim() : DEFAULTS.gleapSdkKey,
    gleapPartnerPortals: normalizeBool(raw?.gleapPartnerPortals, DEFAULTS.gleapPartnerPortals),
    gleapExcludePaths: Array.isArray(raw?.gleapExcludePaths)
      ? raw.gleapExcludePaths.filter((s: unknown): s is string => typeof s === 'string' && s.trim().startsWith('/')).map((s: string) => s.trim())
      : DEFAULTS.gleapExcludePaths,
    gleapSyncLanguage: normalizeBool(raw?.gleapSyncLanguage, DEFAULTS.gleapSyncLanguage),
    gleapClarityLink: normalizeBool(raw?.gleapClarityLink, DEFAULTS.gleapClarityLink),
  };
}

function clampNum(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

function clampBlur(v: unknown): number {
  return Math.round(clampNum(v, 0, 50, DEFAULTS.tutorialBackdropBlurPx));
}

function normalizeRgba(raw: unknown, fallback: AppSettings['tutorialBackdropColor']): AppSettings['tutorialBackdropColor'] {
  if (!raw || typeof raw !== 'object') return fallback;
  const obj = raw as Record<string, unknown>;
  return {
    r: Math.round(clampNum(obj.r, 0, 255, fallback.r)),
    g: Math.round(clampNum(obj.g, 0, 255, fallback.g)),
    b: Math.round(clampNum(obj.b, 0, 255, fallback.b)),
    a: clampNum(obj.a, 0, 1, fallback.a),
  };
}

function readCache(): AppSettings {
  if (typeof window === 'undefined' || !window.localStorage) return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return DEFAULTS;
    return normalize(JSON.parse(raw));
  } catch {
    return DEFAULTS;
  }
}

function writeCache(settings: AppSettings): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Quota / private mode — ignore.
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AppSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(() => readCache());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guards against setState after unmount during the initial fetch.
  const mountedRef = useRef(true);
  // Set on mount as well as cleared on unmount — see the same note in
  // LanguageContext: under the dev server's StrictMode a clear-only ref stays
  // false, and every settings response would be thrown away.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Initial server hydration.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await fetchAppSettings();
        if (cancelled || !mountedRef.current) return;
        const normalized = normalize(fresh);
        setSettings(normalized);
        writeCache(normalized);
        setError(null);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        // Non-fatal — the cache / defaults are still serving the UI.
        const message = err instanceof Error ? err.message : 'Failed to load settings';
        console.warn('[AppSettings]', message);
        setError(message);
      } finally {
        if (!cancelled && mountedRef.current) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pick up changes from other tabs.
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== CACHE_KEY) return;
      setSettings(readCache());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const updateSettings = useCallback(async (patch: Partial<AppSettings>): Promise<AppSettings> => {
    try {
      const merged = await apiUpdateAppSettings(patch);
      if (!mountedRef.current) return merged;
      const normalized = normalize(merged);
      setSettings(normalized);
      writeCache(normalized);
      setError(null);
      return normalized;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      if (mountedRef.current) setError(message);
      throw err;
    }
  }, []);

  const resetSettings = useCallback(async () => {
    return updateSettings(DEFAULTS);
  }, [updateSettings]);

  const value = useMemo<AppSettingsContextValue>(
    () => ({ settings, isLoading, error, updateSettings, resetSettings }),
    [settings, isLoading, error, updateSettings, resetSettings],
  );

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAppSettings(): AppSettingsContextValue {
  const ctx = useContext(AppSettingsContext);
  if (!ctx) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return ctx;
}
